import { ColumnManagerExtendedDef, ColumnManagerExtendedGroupDef } from '@amzn/unified-data-table-components/core';
import { ColDef, ColGroupDef, ValueGetterParams } from '@ag-grid-community/core';
import { VirtualColId } from './constants';
import {
  AccountCell,
  ActionDropdownRenderer,
  CountryCell,
  CovCellRenderer,
  FormatCell,
  NameWithDownloadCellRenderer,
  NextRunCellRenderer,
  ReportCategoryCell,
  ReportPeriodCell,
  ReportTypeCell,
  StatusCellRenderer,
  TimeUnitCell
} from '@urc-ui/pages/landing/subscriptions-table/cell-renderers';
import { TranslateMapConfig } from '@urc-ui/hooks/translate-map';
import { DateTextCellRenderer } from '@urc-ui/components/udc-table/cell-renderers/date-text-renderer';
import React from 'react';
import { HeaderWithTooltip } from '@urc-ui/pages/landing/subscriptions-table/header-with-tooltip';
import { isGlobalUser } from '@urc-ui/utils/app-utils';
import { REPORT_CATEGORY, SUBSCRIPTION_STATUS } from '@urc-ui/utils/types';

/**
 * valueGetter is used to pass more than just a field. params.data contains data for that row.
 */
export enum COLUMN_ID {
  STATUS = 'status',
  REPORT_NAME = 'reportName',
  REPORT_CATEGORY = 'reportCategory',
  REPORT_CREATION_TIMESTAMP = 'reportCreationTimestamp',
  REPORT_NEXT_SCHEDULE_TIME = 'reportNextScheduleTime',
  REPORT_TYPE = 'reportType',
  ACCOUNT = 'accounts',
  REPORT_PERIOD = 'reportPeriod',
  TIME_UNIT = 'timeUnit',
  FORMAT = 'format',
  ACTION = 'action',
  COV = 'currencyConversion',
  COUNTRY = 'country'
}

export const COLUMN_TRANSLATION_MAP: TranslateMapConfig = {
  [COLUMN_ID.STATUS]: {
    id: 'sspa_rcm_ad_reports_column_header_status',
    default: 'Status'
  },
  [COLUMN_ID.REPORT_CREATION_TIMESTAMP]: {
    id: 'sspa_rcm_ad_reports_column_header_last_run',
    default: 'Last run'
  },
  [COLUMN_ID.REPORT_NEXT_SCHEDULE_TIME]: {
    id: 'sspa_rcm_ad_reports_column_header_next_run',
    default: 'Next run'
  },
  [COLUMN_ID.REPORT_NAME]: {
    id: 'sspa_rcm_ad_reports_column_header_report_name',
    default: 'Report name'
  },
  [COLUMN_ID.REPORT_CATEGORY]: {
    id: 'sspa_rcm_ad_reports_column_header_report_category',
    default: 'Report category'
  },
  [COLUMN_ID.REPORT_TYPE]: {
    id: 'sspa_rcm_ad_reports_column_header_report_type',
    default: 'Report type'
  },
  [COLUMN_ID.ACCOUNT]: {
    id: 'sspa_rcm_ad_reports_column_header_account',
    default: 'Account'
  },
  [COLUMN_ID.COV]: {
    id: 'sspa_rcm_ad_reports_column_header_currency_conversion',
    default: 'Currency Conversion'
  },
  [COLUMN_ID.COUNTRY]: {
    id: 'sspa_rcm_ad_reports_country',
    default: 'Country'
  },
  [COLUMN_ID.REPORT_PERIOD]: {
    id: 'sspa_rcm_ad_reports_column_header_report_period',
    default: 'Report period'
  },
  [COLUMN_ID.TIME_UNIT]: {
    id: 'sspa_rcm_ad_reports_column_header_time_unit',
    default: 'Time unit'
  },
  [COLUMN_ID.FORMAT]: {
    id: 'sspa_rcm_ad_reports_column_header_format',
    default: 'Format'
  },
  [COLUMN_ID.ACTION]: {
    id: 'sspa_rcm_ad_reports_column_header_action',
    default: 'Action'
  }
};

export const COLUMN_TOOLTIP_TRANSLATION_MAP: TranslateMapConfig = {
  [COLUMN_ID.COV]: {
    id: 'sspa_rcm_ad_reports_tooltip_text_currency_conversion',
    default:
      'All monetary values in this report are being converted to this currency. The original, non-converted value will also be shown.'
  },
  [COLUMN_ID.COUNTRY]: {
    id: 'sspa_rcm_ad_reports_tooltip_text_country',
    default: 'The countries in the report'
  }
};

// These definitions need to be lifted out so that we can disable lint checks on the pinned field
// https://tropical.advertising.amazon.dev/tropical-storybook/iframe.html?id=tropical-ui-version-upgrades--upgrade-to-tropical-3&viewMode=story
// 8. Type of property pinned in ColDef is updated
const STATUS_COLUMN: ColDef = {
  colId: COLUMN_ID.STATUS,
  sortable: true,
  filter: 'agSetColumnFilter',
  filterParams: {
    values: Object.values(SUBSCRIPTION_STATUS)
  },
  cellRenderer: StatusCellRenderer,
  valueGetter: (params: ValueGetterParams): any => ({ subscription: params.data }),
  width: 200,
  maxWidth: 200,
  minWidth: 200,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  pinned: 'start'
};

const REPORT_NAME_COLUMN: ColDef = {
  colId: COLUMN_ID.REPORT_NAME,
  sortable: true,
  field: 'name',
  cellRenderer: NameWithDownloadCellRenderer,
  valueGetter: (params: ValueGetterParams): any => ({ subscription: params.data }),
  minWidth: 410,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  pinned: 'start'
};

export const columnDefinitions = (headerMap: Record<string, string>): Array<ColDef | ColGroupDef> => {
  const globalOnlyColumns = isGlobalUser()
    ? [
        {
          colId: COLUMN_ID.COUNTRY,
          cellRenderer: CountryCell,
          headerComponentParams: {
            label: <HeaderWithTooltip colId={COLUMN_ID.COUNTRY} />
          },
          field: 'marketplaceIds',
          hide: false,
          width: 150,
          maxWidth: 150,
          minWidth: 150
        },
        {
          colId: COLUMN_ID.COV,
          cellRenderer: CovCellRenderer,
          headerComponentParams: {
            label: <HeaderWithTooltip colId={COLUMN_ID.COV} />
          },
          field: 'currencyOfVisualization',
          hide: false,
          width: 150,
          maxWidth: 150,
          minWidth: 150
        }
      ]
    : [];

  const adspOnlyColumns =
    window.AppContext?.isADSPReportCenterR1Enabled === 'true'
      ? {
          colId: COLUMN_ID.ACCOUNT,
          cellRenderer: AccountCell,
          valueGetter: (params: ValueGetterParams) => ({ subscription: params.data, api: params.api }),
          hide: true,
          width: 150,
          maxWidth: 150,
          minWidth: 150
        }
      : [];

  return [
    STATUS_COLUMN,
    REPORT_NAME_COLUMN,
    {
      colId: COLUMN_ID.REPORT_CREATION_TIMESTAMP,
      field: 'creationTimestamp',
      cellRenderer: DateTextCellRenderer,
      width: 150,
      maxWidth: 150,
      minWidth: 150
    },
    {
      colId: COLUMN_ID.REPORT_NEXT_SCHEDULE_TIME,
      field: 'nextScheduleTime',
      cellRenderer: NextRunCellRenderer,
      valueGetter: (params: ValueGetterParams): any => ({ status: params.data?.status, nextScheduleTime: params.data?.nextScheduleTime }),
      width: 150,
      maxWidth: 150,
      minWidth: 150
    },
    {
      colId: COLUMN_ID.REPORT_CATEGORY,
      field: 'programType',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: Object.values(REPORT_CATEGORY)
      },
      cellRenderer: ReportCategoryCell,
      width: 180,
      maxWidth: 180,
      minWidth: 180
    },
    {
      colId: COLUMN_ID.REPORT_TYPE,
      cellRenderer: ReportTypeCell,
      valueGetter: (params: ValueGetterParams) => ({
        programType: params.data.programType,
        reportType: params.data.reportType
      }),
      width: 300,
      maxWidth: 300,
      minWidth: 300
    }
  ]
    .concat(adspOnlyColumns)
    .concat(globalOnlyColumns)
    .concat([
      {
        colId: COLUMN_ID.REPORT_PERIOD,
        cellRenderer: ReportPeriodCell,
        width: 237,
        maxWidth: 237,
        minWidth: 237
      },
      {
        colId: COLUMN_ID.TIME_UNIT,
        cellRenderer: TimeUnitCell,
        valueGetter: (params: ValueGetterParams) => params.data.timeUnit,
        width: 150,
        maxWidth: 150,
        minWidth: 150
      },
      {
        colId: VirtualColId.QuickFilterVirtualColumnId,
        filter: 'agTextColumnFilter',
        hide: true,
        lockVisible: true
      },
      {
        colId: COLUMN_ID.FORMAT,
        cellRenderer: FormatCell,
        valueGetter: (params: ValueGetterParams) => params.data.format,
        hide: true,
        width: 80,
        maxWidth: 80,
        minWidth: 80
      },
      {
        colId: COLUMN_ID.ACTION,
        cellRenderer: ActionDropdownRenderer,
        valueGetter: (params: ValueGetterParams) => ({
          subscription: params.data,
          api: params.api
        }),
        width: 108,
        maxWidth: 108,
        minWidth: 108
      }
    ])
    .map(column => ({
      ...column,
      ...(column.colId && headerMap[column.colId] ? { headerName: headerMap[column.colId] } : {})
    }));
};

export const columnExtendedDefinitions = (): Array<ColumnManagerExtendedDef | ColumnManagerExtendedGroupDef> => {
  const globalOnlyColumns = isGlobalUser()
    ? [
        {
          colId: COLUMN_ID.COUNTRY,
          alwaysHidden: false,
          alwaysDisplayed: false,
          hideWhenResetToDefault: false
        },
        {
          colId: COLUMN_ID.COV,
          alwaysHidden: false,
          alwaysDisplayed: false,
          hideWhenResetToDefault: false
        }
      ]
    : [];

  const adspOnlyColumns =
    window.AppContext?.isADSPReportCenterR1Enabled === 'true'
      ? [
          {
            colId: COLUMN_ID.ACCOUNT,
            alwaysHidden: false,
            alwaysDisplayed: false,
            hideWhenResetToDefault: true
          }
        ]
      : [];

  return [
    {
      colId: COLUMN_ID.STATUS as string,
      alwaysHidden: false,
      alwaysDisplayed: true,
      hideWhenResetToDefault: false
    },
    {
      colId: COLUMN_ID.REPORT_NAME,
      alwaysHidden: false,
      alwaysDisplayed: true,
      hideWhenResetToDefault: false
    },
    {
      colId: COLUMN_ID.REPORT_CREATION_TIMESTAMP,
      alwaysHidden: false,
      alwaysDisplayed: false,
      hideWhenResetToDefault: false
    },
    {
      colId: COLUMN_ID.REPORT_NEXT_SCHEDULE_TIME,
      alwaysHidden: false,
      alwaysDisplayed: false,
      hideWhenResetToDefault: false
    },
    {
      colId: COLUMN_ID.REPORT_CATEGORY,
      alwaysHidden: false,
      alwaysDisplayed: true,
      hideWhenResetToDefault: false
    },
    {
      colId: COLUMN_ID.REPORT_TYPE,
      alwaysHidden: false,
      alwaysDisplayed: true,
      hideWhenResetToDefault: false
    }
  ]
    .concat(adspOnlyColumns)
    .concat(globalOnlyColumns)
    .concat([
      {
        colId: COLUMN_ID.REPORT_PERIOD,
        alwaysHidden: false,
        alwaysDisplayed: true,
        hideWhenResetToDefault: false
      },
      {
        colId: COLUMN_ID.TIME_UNIT,
        alwaysHidden: false,
        alwaysDisplayed: true,
        hideWhenResetToDefault: false
      },
      {
        colId: COLUMN_ID.FORMAT,
        alwaysHidden: false,
        alwaysDisplayed: false,
        hideWhenResetToDefault: true
      },
      {
        colId: COLUMN_ID.ACTION,
        alwaysHidden: false,
        alwaysDisplayed: true,
        hideWhenResetToDefault: false
      },
      {
        colId: VirtualColId.QuickFilterVirtualColumnId as string,
        alwaysHidden: true,
        alwaysDisplayed: false,
        hideWhenResetToDefault: true
      }
    ]);
};
